import { FC, useState } from 'react'

import { Icons, Progress, Text } from '@agro-club/agroclub-shared'

import { useAProgress } from 'hooks/useAProgress'
import { useNotification } from 'hooks/useNotification'
import { refetchFunc } from 'modules/domain/common/hooks'
import { Driver, DriverStatuses } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { useTranslation } from 'react-i18next'
import { AButton } from 'views/components/Analytics'
import ADefaultModalFooter from 'views/components/DefaultModalFooter/ADefaultModalFooter'
import { SharedModalContent, SharedModalTitle } from 'views/components/SharedModal'
import ASharedModal from 'views/components/SharedModal/ASharedModal'
import { AnalyticsPlaces } from 'views/pages/LogisticsKR/analyticsPlaces'
import { DriverEditModal } from 'views/pages/LogisticsKR/components/DriverModals/DriverEditModal'
import { DetailsItemButtons, DetailsItemInfo } from 'views/pages/LogisticsKR/components/styled'
import APhone from 'views/ui/Phone/APhone'
import { LogisticsVerifiedIcon } from '../../LogisticsVerifiedIcon'
import useFormatDate from 'hooks/useFormatDate'
import styled from 'styled-components'
import { Card } from 'views/components/Card/Card'
import { DriverDocuments } from '../CarrierAccordion/DriversDocuments/DriverDocuments'

interface Props {
  driver: Driver
  carrierId: string
  refetch: refetchFunc
}

const NameWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

export const DriverItem: FC<Props> = ({ driver, carrierId, refetch }) => {
  const { t } = useTranslation('logisticsKR')
  const notify = useNotification()
  const formatDate = useFormatDate()
  const [isModalOpened, setIsModalOpened] = useState(false)
  const [removeConfirmModal, setRemoveConfirmModal] = useState(false)
  const isArchive = driver.status === DriverStatuses.archived

  const [removeProgress, remove] = useAProgress(
    (driverId) => apiClient.delete(endpoints.LogisticsKR.drivers(driverId)),
    {
      eventName: 'removeDriver',
      onSuccess: async () => {
        await refetch()
        setRemoveConfirmModal(false)
        notify(Progress.SUCCESS, { title: t('carriers.notifications.driver_archived_success') })
      },
      onError: () => notify(Progress.ERROR, { title: t('carriers.notifications.driver_archived_error') }),
    },
  )

  return (
    <>
      <Card.Container style={{ minHeight: '215px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <DetailsItemInfo>
            <NameWrapper>
              <Text color={isArchive ? 'secondary' : 'primary'}>
                {driver.name} {isArchive ? t('carriers.archived') : ''}
              </Text>
              {driver.verified_by_security_at && (
                <LogisticsVerifiedIcon
                  isVerified={!isArchive && !!driver.verified_by_security_at}
                  hintText={`${t('carriers.verifiedBySecurity')} ${formatDate(driver.verified_by_security_at)}`}
                  size="small"
                />
              )}
            </NameWrapper>
            {isArchive ? <Text color="tertiary">{driver.phone}</Text> : <APhone phone={driver.phone} />}
          </DetailsItemInfo>
          {!isArchive && (
            <DetailsItemButtons>
              <AButton
                id="editDriver"
                Icon={Icons.IconEdit}
                onClick={() => setIsModalOpened(true)}
                color="secondary"
                background="none"
              />
              <AButton
                id="removeDriver"
                Icon={Icons.IconDelete}
                onClick={() => setRemoveConfirmModal(true)}
                color="secondary"
                background="none"
              />
            </DetailsItemButtons>
          )}
        </div>
        <DriverDocuments key={driver.id} driverId={parseInt(driver.id)} />
      </Card.Container>

      {isModalOpened && (
        <DriverEditModal
          onClose={() => setIsModalOpened(false)}
          carrierId={carrierId}
          onSuccess={refetch}
          driver={driver}
        />
      )}

      {removeConfirmModal && (
        <ASharedModal
          id={AnalyticsPlaces.CARRIERS.DELETE_DRIVER_MODAL}
          size="small"
          onClose={() => setRemoveConfirmModal(false)}
        >
          <SharedModalTitle>{t('carriers.confirmModals.archivedDriver')}</SharedModalTitle>
          <SharedModalContent>{t('carriers.confirmModals.youReallyWantArchivedDriver')}</SharedModalContent>
          <ADefaultModalFooter
            progress={removeProgress}
            onClose={() => setRemoveConfirmModal(false)}
            onConfirm={() => remove(driver.id)}
          />
        </ASharedModal>
      )}
    </>
  )
}
